import { Icon } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { Suspense, useMemo } from 'react';
import type { ShellAppHeaderProps } from 'web_mfe_shell/ShellAppHeader';

import { PageLayout } from '../../../components/ui/PageLayout';
import { useContent } from '../../../hooks/useContent';
import { useDerivedPropertySlug } from '../../../hooks/useDerivedPropertySlug';
import { useMobile } from '../../../hooks/useMediaQuery';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';

import {
  StyledHeading,
  StyledBody,
  StyledContactInfo,
  StyledLabel,
  StyledContentWrapper,
  StyledFooterContainer,
  StyledContainer,
  StyledTextLink,
} from './styles';

const ShellGlobalFooter = React.lazy(() => import('web_mfe_shell/GlobalFooter'));
const ShellAppHeader = React.lazy(() => import('web_mfe_shell/ShellAppHeader'));

export function ContactSupport() {
  const isMobile = useMobile();
  usePageLoadEvent('account access', 'contact support');
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();

  const property = useDerivedPropertySlug();

  const handlePhoneClick = async () => {
    await trackEvent('identity – unified MFA CTAs', 'call - contact support', 'unifiedmfactas', {}, true);
  };

  const desktopConfig = useMemo<ShellAppHeaderProps['desktopConfig']>(
    () => ({
      enableCartIcon: true,
      enableExploreResorts: false,
      enableGlowAnimation: false,
      enableSearch: false,
      enableSecondaryNav: false,
      enableSignIn: false,
      enableSiteLogo: true,
    }),
    [],
  );

  const mobileConfig = useMemo<ShellAppHeaderProps['mobileConfig']>(
    () => ({
      displayMobileBackButton: true,
      enableCartIcon: true,
      enableExploreResorts: false,
      enableGlowAnimation: false,
      enableSearch: false,
      enableSecondaryNav: false,
      enableSidebarMenus: false,
      enableSignIn: false,
      enableSiteLogo: true,
      enableMenu: false,
      onMobileBackButtonClick: () => {
        navigate('/');
      },
    }),
    [],
  );

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <ShellAppHeader
          property={property}
          desktopConfig={desktopConfig}
          mobileConfig={mobileConfig}
          cartVariant={'icon'}
        />
      </Suspense>

      <PageLayout
        testId='route-/mfa/contact-support'
        isContactSupport={true}
        title={getContentByPath('contactSupport.title')}
      >
        <StyledContainer height='auto' width='50%' gap='24px'>
          <StyledContentWrapper>
            <StyledHeading variant='heading-medium-4' as='h1' testId='contact-support-page-title'>
              {getContentByPath('contactSupport.title')}
            </StyledHeading>
            <StyledLabel variant='label-medium-l' as='h2' testId='contact-support-page-subtitle'>
              {getContentByPath('contactSupport.description')}
            </StyledLabel>

            <StyledBody variant='body-regular-m' testId='mfa-recovery-body'>
              {getContentByPath('contactSupport.body')}
            </StyledBody>

            <StyledContactInfo>
              <Icon name='phone-analog' variant='outlined' size='small' />
              <StyledLabel variant='label-medium-l' testId='contact-phone-message'>
                {getContentByPath('contactSupport.phoneMessage')}
              </StyledLabel>
              <StyledTextLink href='tel:866-761-7111' variant='large' testId='contact-phone' onClick={handlePhoneClick}>
                {getContentByPath('contactSupport.phoneNumber')}
              </StyledTextLink>
            </StyledContactInfo>
          </StyledContentWrapper>
        </StyledContainer>
      </PageLayout>

      <Suspense fallback={<div>Loading...</div>}>
        <StyledFooterContainer $isMobile={isMobile}>
          <ShellGlobalFooter property={property} footerType='text' />
        </StyledFooterContainer>
      </Suspense>
    </>
  );
}

ContactSupport.displayName = 'ContactSupport';
